/**
 * The Show — an EVENT: one venue (one stage), one date, the acts that play it, the stage's own input
 * numbering and the physical equipment on it (SCRUM-587, `docs/V2_SHOW_MINIMAL.md`).
 *
 * Not the festival. Owner, 05.10.2026: «это не фестивальное шоу это общий случай шоу». A solo club
 * gig is a complete Show with no field left empty "because it isn't a festival"; a party with three
 * bands is the same Show with three performances — «фестивальное отличается количеством выступающих …
 * но все коннекторы под него надо создать». The archived festival schema (tag
 * `archive/scrum-189-festival-show`) is what plugs in later, not what this is.
 *
 * What the shape carries, each because the owner said so (the numbers are the doc's §1 table):
 *
 * - PERFORMANCES, never a single `rider` (#2, #3): a second act is a second entry, not a migration;
 * - the stage's own INPUT table, apart from every rider's numbering (#4, «если появляются три группы
 *   то у нас появляется пространство номеров», confirmed «это правильно»): three bands each say
 *   «kick is 1», and the stage gives each kick its own input, or the same one at another time. The
 *   map "rider line → stage input" lives on the performance. For one act both are empty and its own
 *   numbering IS the stage's;
 * - equipment INSTANCES (#8–#10): kit A, kit B, kit C — not a catalogue line with a count. «на 6 групп
 *   может быть 3 ударные установки. 2 которыми пользуются 5 групп по очереди и одна персональная
 *   хэдлайнера». WHO BROUGHT IT and WHO MAY USE IT are two independent axes (#9);
 * - a route written the same way in both domains (#11–#12, «патч переезжает … внутрь протокола …
 *   но он обязан быть отражен»): a Dante box still has a box and a socket;
 * - a date that is one of four kinds (#13, §4g): open, a range (possibly with days taken out —
 *   «последняя неделя ноября, без первых дней недели»), a choice of days, or a fixed day.
 *
 * What it deliberately does NOT carry: offers (they live in their own table and point at the Show),
 * per-person read/archived state (one Show, never two copies — per-party rows on the server), and
 * its own state (open / waiting / offers in / agreed / past is DERIVED, `deriveShowState`).
 *
 * This package knows nothing of persistence; `apps/api` owns the table.
 */
import { z } from "zod";
import { UuidSchema } from "./schema";
export const SHOW_SCHEMA_VERSION = 1;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const IsoDaySchema = z.string().regex(ISO_DAY, "a date as YYYY-MM-DD");
const LocalTimeSchema = z.string().regex(LOCAL_TIME, "a local time as HH:MM");
// ---------------------------------------------------------------------------------------------
// When — four kinds, each a STATE rather than a half-filled field (§4g)
// ---------------------------------------------------------------------------------------------
/** Nothing agreed yet. A normal state: the Show waits for a date and for offers. */
const OpenWhenSchema = z.object({ kind: z.literal("open") }).strict();
/**
 * A held range — «in November», «1–15 Nov» — with days that may be taken out: whole weekdays
 * (`0` = Sunday … `6` = Saturday, as `Date.getUTCDay`) or single dates. `said` keeps the person's own
 * words for lists and letters, because «last week of November, Wed–Sat» reads better than four dates.
 */
const WindowWhenSchema = z
    .object({
    kind: z.literal("window"),
    from: IsoDaySchema,
    to: IsoDaySchema,
    except_weekdays: z.array(z.number().int().min(0).max(6)).max(6).optional(),
    except_dates: z.array(IsoDaySchema).max(366).optional(),
    said: z.string().max(200).optional(),
})
    .strict();
/** A few candidate days — «14 or 21 Nov». One day is a `day`, not a choice. */
const ChoiceWhenSchema = z
    .object({ kind: z.literal("choice"), dates: z.array(IsoDaySchema).min(2).max(31), said: z.string().max(200).optional() })
    .strict();
/** A fixed day, with the local times the day sheet needs. Times are the VENUE's local times. */
const DayWhenSchema = z
    .object({
    kind: z.literal("day"),
    date: IsoDaySchema,
    load_in: LocalTimeSchema.optional(),
    soundcheck: LocalTimeSchema.optional(),
    doors: LocalTimeSchema.optional(),
    curfew: LocalTimeSchema.optional(),
})
    .strict();
export const ShowWhenSchema = z.discriminatedUnion("kind", [OpenWhenSchema, WindowWhenSchema, ChoiceWhenSchema, DayWhenSchema]);
/**
 * The supplier's ANSWER «date and venue not set yet» (SCRUM-634, owner 06.10.2026). Stored in
 * `offers.offered_when` as `{ kind: "open", said: "not_set" }` — the existing `open` kind plus an explicit
 * marker, so «the supplier said not set» is told apart from a row where nothing was ever asked
 * (`offered_when` null, or a bare `{ kind: "open" }` from before the question existed). It is NOT a Show's
 * date: a Show's `when` stays a plain `ShowWhen`; `showWhenOf` turns an offered date back into one.
 */
export const NotSetAnswerSchema = z.object({ kind: z.literal("open"), said: z.literal("not_set") }).strict();
export const NOT_SET_ANSWER = { kind: "open", said: "not_set" };
/** What `offers.offered_when` holds: a date as the supplier gave it, or the explicit «not set yet». */
export const OfferedWhenSchema = z.union([ShowWhenSchema, NotSetAnswerSchema]);
/** True only for the supplier's explicit «not set yet» — never for null or a bare `open` (legacy: never asked). */
export function isNotSetAnswer(when) {
    return typeof when === "object" && when !== null && when.kind === "open" && when.said === "not_set";
}
/** The Show-shaped date of an offered one: the «not set yet» answer is an `open` date. */
export function showWhenOf(when) {
    if (!when)
        return { kind: "open" };
    return isNotSetAnswer(when) ? { kind: "open" } : when;
}
// ---------------------------------------------------------------------------------------------
// The stage's inputs (the master patch rows) and their routes
// ---------------------------------------------------------------------------------------------
/** The transports a stage box can sit on. A route on a network still names its box and socket. */
export const SHOW_NETWORK_PROTOCOLS = [
    "dante",
    "aes50",
    "madi",
    "soundgrid",
    "avb",
    "optocore",
    "stageconnect",
    "gigaace",
    "other",
];
export const ShowInputRouteSchema = z
    .object({
    /** The box's name or code as taped on its lid — "DRUM-RIO", "SL-MULTI". */
    stagebox: z.string().min(1).max(100).optional(),
    socket: z.number().int().min(1).optional(),
    network: z
        .object({ protocol: z.enum(SHOW_NETWORK_PROTOCOLS), stream: z.string().max(200).optional() })
        .strict()
        .optional(),
})
    .strict();
export const ShowInputSchema = z
    .object({
    uuid: UuidSchema,
    /** The STAGE's own number for this input — unique within the Show (`validateShow`). */
    number: z.number().int().min(1),
    label: z.string().max(200).optional(),
    route: ShowInputRouteSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Performances
// ---------------------------------------------------------------------------------------------
/** How an act is paid decides, by default, whether the organiser shares the money with it (§4c). */
export const SHOW_DEAL_KINDS = ["fee", "percentage", "other"];
export const ShowPerformanceSchema = z
    .object({
    uuid: UuidSchema,
    /** The act as the Show names it. */
    name: z.string().min(1).max(200),
    /** Running order; 0 for a single act. */
    order: z.number().int().min(0),
    /**
     * The rider this act plays — a PUBLISHED version, so what was asked cannot move under the offers
     * answering it. Absent: the act is booked, its rider has not arrived (an ordinary state — the
     * organiser's first button is «Request rider», §4d).
     */
    rider_id: UuidSchema.optional(),
    rider_version: z.number().int().min(1).optional(),
    /** When the act adapted its rider for this Show (§4e), the published rider the copy came from. */
    adapted_from: z.object({ rider_id: UuidSchema, rider_version: z.number().int().min(1) }).strict().optional(),
    rider_request: z
        .object({
        to_email: z.string().email().max(320),
        sent_at: z.string().max(40),
        state: z.enum(["sent", "answered", "declined"]),
    })
        .strict()
        .optional(),
    set_start: LocalTimeSchema.optional(),
    set_minutes: z.number().int().min(0).max(24 * 60).optional(),
    /** "This act's input line `channel` lands on stage input `input`." Empty for one act. */
    line_map: z.array(z.object({ channel: z.number().int().min(1), input: UuidSchema }).strict()),
    deal: z.object({ kind: z.enum(SHOW_DEAL_KINDS), finance_shared: z.boolean() }).strict().optional(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Equipment instances
// ---------------------------------------------------------------------------------------------
export const EquipmentInstanceSchema = z
    .object({
    uuid: UuidSchema,
    /** "Kit A", "Ampeg SVT-CL (guitarist's own)". */
    name: z.string().min(1).max(200),
    /** Catalogue link, the same way a gear line links (SCRUM-120). */
    equipment_ids: z.array(z.number().int().min(1)).max(50).optional(),
    /** Who brings it — an offer's supplier, an act, or not settled yet. Independent of `used_by`. */
    brought_by: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("offer"), offer_id: UuidSchema }).strict(),
        z.object({ kind: z.literal("performance"), performance: UuidSchema }).strict(),
        z.object({ kind: z.literal("unknown") }).strict(),
    ]),
    /** The acts that use it, in turn. EMPTY means everyone on the Show shares it. */
    used_by: z.array(UuidSchema),
    /** «не хочет чтобы его кто то трогал» — exactly one act uses it (`validateShow`). Words, not a lock. */
    exclusive: z.boolean().optional(),
    comment: z.string().max(2000).optional(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// The Show
// ---------------------------------------------------------------------------------------------
export const ShowSchema = z
    .object({
    schema_version: z.literal(SHOW_SCHEMA_VERSION),
    uuid: UuidSchema,
    /** Who created it — the only always-present fact besides the uuid. */
    owner: UuidSchema,
    title: z.string().max(255).optional(),
    when: ShowWhenSchema,
    /** Absent = not decided yet. A venue is its own row (name, city, time zone, stage, own sound). */
    venue_id: UuidSchema.optional(),
    performances: z.array(ShowPerformanceSchema).max(100),
    inputs: z.array(ShowInputSchema).max(512),
    equipment: z.array(EquipmentInstanceSchema).max(1000),
    notes: z.string().max(5000).optional(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------------------------
function dayAfter(iso) {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
}
function weekday(iso) {
    return new Date(`${iso}T00:00:00Z`).getUTCDay();
}
/** The days the Show may fall on, ascending. Open → none; a range → its days minus the ones taken out. */
export function candidateDays(when) {
    switch (when.kind) {
        case "open":
            return [];
        case "day":
            return [when.date];
        case "choice":
            return [...new Set(when.dates)].sort();
        case "window": {
            if (when.to < when.from)
                return [];
            const skipDays = new Set(when.except_weekdays ?? []);
            const skipDates = new Set(when.except_dates ?? []);
            const out = [];
            for (let d = when.from; d <= when.to && out.length <= 366; d = dayAfter(d)) {
                if (!skipDays.has(weekday(d)) && !skipDates.has(d))
                    out.push(d);
            }
            return out;
        }
    }
}
export function validateShow(input) {
    const parsed = ShowSchema.safeParse(input);
    if (!parsed.success) {
        return { ok: false, errors: parsed.error.issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`) };
    }
    const s = parsed.data;
    const errors = [];
    const when = s.when;
    if (when.kind === "window") {
        if (when.to < when.from)
            errors.push("when: the range ends before it starts");
        else if (candidateDays(when).length === 0)
            errors.push("when: every day of the range is taken out");
    }
    const performances = new Set();
    for (const p of s.performances) {
        if (performances.has(p.uuid))
            errors.push(`performances: ${p.uuid} appears twice`);
        performances.add(p.uuid);
        if ((p.rider_id === undefined) !== (p.rider_version === undefined)) {
            errors.push(`performance ${p.name}: a rider needs its published version, and a version its rider`);
        }
    }
    const inputs = new Set();
    const numbers = new Set();
    for (const i of s.inputs) {
        if (inputs.has(i.uuid))
            errors.push(`inputs: ${i.uuid} appears twice`);
        inputs.add(i.uuid);
        if (numbers.has(i.number))
            errors.push(`inputs: stage number ${i.number} is used twice`);
        numbers.add(i.number);
    }
    for (const p of s.performances) {
        const channels = new Set();
        for (const m of p.line_map) {
            if (!inputs.has(m.input))
                errors.push(`performance ${p.name}: channel ${m.channel} maps to an input that is not on the stage`);
            if (channels.has(m.channel))
                errors.push(`performance ${p.name}: channel ${m.channel} is mapped twice`);
            channels.add(m.channel);
        }
    }
    for (const e of s.equipment) {
        for (const user of e.used_by) {
            if (!performances.has(user))
                errors.push(`equipment ${e.name}: used by an act that is not on this Show`);
        }
        if (e.brought_by.kind === "performance" && !performances.has(e.brought_by.performance)) {
            errors.push(`equipment ${e.name}: brought by an act that is not on this Show`);
        }
        if (e.exclusive && e.used_by.length !== 1)
            errors.push(`equipment ${e.name}: exclusive means exactly one act uses it`);
    }
    return errors.length === 0 ? { ok: true, show: s } : { ok: false, errors };
}
// ---------------------------------------------------------------------------------------------
// Derived facts
// ---------------------------------------------------------------------------------------------
export const SHOW_STATES = ["open", "waiting", "offers_in", "agreed", "past"];
/**
 * The Show's state, never stored (§2): it cannot be "past" with tomorrow's date or "agreed" with no
 * accepted offer. Only a FIXED day can pass — a held range or a choice waits to be narrowed.
 * `today` is the venue's local date as `YYYY-MM-DD`.
 */
export function deriveShowState(show, offers, today) {
    if (show.when.kind === "day" && show.when.date < today)
        return "past";
    if (offers.accepted > 0)
        return "agreed";
    if (offers.submitted > 0)
        return "offers_in";
    if (show.performances.length > 0)
        return "waiting";
    return "open";
}
//# sourceMappingURL=show.js.map